import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const ItemDetails = () => {
  const location = useLocation();
  const [itemInfo, setItemInfo] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [alert, setAlert] = useState("");
  const [loading, setLoading] = useState(true);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

const queryParams = new URLSearchParams(location.search);
  const itemid = queryParams.get("itemid");

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": branchId,
    "x-branch": branch,
    "x-userid": userId,
  };

  useEffect(() => {
    const fetchItemDetails = async () => {
      try {
        setLoading(true);

     const [basicRes, qtyRes] = await Promise.all([
          axios.get(`http://nissi-india.com/api/itemBasicDetails/${itemid}`, {
            headers: commonHeaders,
          }),
          axios.get(`http://nissi-india.com/api/itemQtyAllBranch/${itemid}`, {
            headers: commonHeaders,
          }),
        ]);

if (basicRes.data?.data) {
          setItemInfo(basicRes.data.data);
        } else {
          setAlert("Item details not found.");
        }

        const qtyList = qtyRes.data?.data;
        if (Array.isArray(qtyList) && qtyList.length > 0) {
          setStockData(qtyList);
        } else {
          setAlert((prev) => prev || "No branch-wise stock available.");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setAlert("Failed to load item data.");
      } finally {
        setLoading(false);
      }
    };

    if (itemid) {
      fetchItemDetails();
    } else {
      setAlert("No item ID provided.");
      setLoading(false);
    }
  }, [itemid]);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Item Details</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Item Stock Details</h1>
              <p className={`text-sm mt-0.5 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                View complete information and branch-wise stock
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div
            className={`p-8 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <Skeleton height={30} width={200} className="mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height={100} borderRadius={12} />
              ))}
            </div>
            <Skeleton height={250} borderRadius={12} />
          </div>
        ) : alert ? (
          <div
            className={`p-8 rounded-2xl shadow-xl border text-center ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-lg font-semibold text-red-500">{alert}</p>
          </div>
        ) : (
          <>
            {/* Item Info Cards */}
            <div
              className={`p-6 rounded-2xl shadow-xl border ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                  : `bg-white ${currentSkin.glass} text-black border-gray-200`
              }`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h2 className="text-xl font-bold">Item Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Item Name */}
                <div
                  className={`p-5 rounded-xl border-2 transition-all ${
                    isDark
                      ? "bg-gray-800/40 border-gray-600"
                      : "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100"
                  }`}
                >
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Item Name
                  </div>
                  <div className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    {itemInfo?.fld_itemname || "N/A"}
                  </div>
                </div>

                {/* Item Code */}
                <div
                  className={`p-5 rounded-xl border-2 transition-all ${
                    isDark
                      ? "bg-gray-800/40 border-gray-600"
                      : "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100"
                  }`}
                >
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Item Code
                  </div>
                  <div className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    {itemInfo?.fld_itemcode || "N/A"}
                  </div>
                </div>

                {/* Part No */}
                <div
                  className={`p-5 rounded-xl border-2 transition-all ${
                    isDark
                      ? "bg-gray-800/40 border-gray-600"
                      : "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100"
                  }`}
                >
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Part Number
                  </div>
                  <div className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    {itemInfo?.fld_itemPartNo || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Branch-wise Stock Table */}
            <div
              className={`rounded-2xl shadow-xl border overflow-hidden ${
                isDark
                  ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                  : `bg-white ${currentSkin.glass} border-gray-200`
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                  <h2 className="text-xl font-bold">Branch-wise Stock</h2>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">S.No</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Branch</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Quantity</th>
                    </tr>
                  </thead>

                  <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                    {stockData.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                          No stock data available
                        </td>
                      </tr>
                    ) : (
                      stockData.map((row, index) => (
                        <tr
                          key={index}
                          className={`transition-colors ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-6 py-4 font-medium">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-6 py-4 font-medium">
                            {row.branchName || "N/A"}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                Number(row.quantity) > 0
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {row.quantity ?? 0}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Summary Footer */}
              {stockData.length > 0 && (
                <div
                  className={`px-6 py-4 border-t flex items-center justify-between ${
                    isDark ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Total Branches: <span className="font-bold">{stockData.length}</span>
                  </div>
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Total Quantity:{" "}
                    <span className="font-bold text-blue-600">
                      {stockData.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default ItemDetails;